import React, { useState, useMemo } from 'react';
import { Slider } from '../ui/slider';
import { cn } from '../../lib/utils';
import { ArcaPlan } from '../../constants/arcaPlans';
import { fmtEur, fmtNum } from '../../utils/formatArcaNumbers';
import { projectMonthly } from '../../utils/monthlyProjection';
import { PlanReturnView } from '../../utils/transferReturns';

interface ExplorationProjectionV2Props {
	translate: (key: string) => string;
	plans: ArcaPlan[];
	/** Rentabilidad de cada plan por slug, de comparsa. Sin entrada o sin mapa, «sin datos». */
	planReturns: Record<string, PlanReturnView | null> | undefined;
}

const RETIREMENT_AGE = 67;
const DEFAULT_AGE = 35;
const DEFAULT_MONTHLY = 100;

const ExplorationProjectionV2: React.FC<ExplorationProjectionV2Props> = ({
	translate,
	plans,
	planReturns
}) => {
	const [age, setAge] = useState(DEFAULT_AGE);
	const [monthly, setMonthly] = useState(DEFAULT_MONTHLY);

	const projections = useMemo(() => {
		const years = Math.max(1, RETIREMENT_AGE - age);
		// Mes a mes con la tasa NETA de comparsa: la comisión ya está descontada (antes se restaba
		// otra vez). Sin datos del plan, no se proyecta.
		return plans.map((plan) => {
			const planReturn = planReturns?.[plan.catalogSlug] ?? null;
			const result = planReturn ? projectMonthly(0, monthly, planReturn.monthlyPct, years) : null;
			return { plan, planReturn, result };
		});
	}, [age, monthly, plans, planReturns]);

	return (
		<div className="space-y-6">
			<div className="space-y-4">
				<div className="space-y-2">
					<div className="flex justify-between text-sm">
						<span className="text-muted-foreground">
							{translate('individual.projection.age') || 'Tu edad'}
						</span>
						<span className="font-semibold">{age} años</span>
					</div>
					<div className="slider-arca">
						<Slider min={18} max={65} step={1} value={[age]} onValueChange={([v]) => setAge(v)} />
					</div>
					<div className="flex justify-between text-xs text-muted-foreground">
						<span>18</span>
						<span>65</span>
					</div>
				</div>

				<div className="space-y-2">
					<div className="flex justify-between text-sm">
						<span className="text-muted-foreground">
							{translate('individual.projection.monthly') || 'Aportación mensual'}
						</span>
						<span className="font-semibold">{monthly} €/mes</span>
					</div>
					<div className="slider-arca">
						<Slider
							min={25}
							max={600}
							step={25}
							value={[monthly]}
							onValueChange={([v]) => setMonthly(v)}
						/>
					</div>
					<div className="flex justify-between text-xs text-muted-foreground">
						<span>25 €</span>
						<span>600 €</span>
					</div>
				</div>
			</div>

			<p className="text-xs text-muted-foreground">
				{translate('individual.projection.disclaimer') ||
					`Proyección hasta los ${RETIREMENT_AGE} años (${Math.max(1, RETIREMENT_AGE - age)} años). Rentabilidades pasadas no garantizan resultados futuros.`}
			</p>

			<div className="space-y-3">
				{projections.map(({ plan, planReturn, result }) => (
					<div key={plan.id} className="surface-soft p-4 flex items-center justify-between gap-4">
						<div className="flex items-center gap-3 min-w-0">
							<img
								src={plan.logo}
								alt={plan.manager}
								className="w-8 h-8 rounded-lg object-contain shrink-0 bg-white border border-border p-0.5"
							/>
							<div className="min-w-0">
								<p className="text-sm font-medium truncate">{plan.name}</p>
								<p className="text-xs text-muted-foreground">{plan.style}</p>
							</div>
						</div>
						<div className="text-right shrink-0">
							{planReturn && result !== null ? (
								<>
									<p className={cn('text-lg font-bold', 'text-foreground')}>{fmtEur(result)}</p>
									{planReturn.managementFeePct !== null && (
										<p className="text-xs text-muted-foreground">
											{fmtNum(planReturn.managementFeePct)}%{' '}
											{translate('individual.otherPlans.feeMgmt') || 'comisión de gestión'}
										</p>
									)}
								</>
							) : (
								<p className="text-xs text-muted-foreground">
									{planReturns === undefined
										? translate('individual.exploration.chosenPlan.loadingReturns') ||
											'Cargando rentabilidad…'
										: translate('individual.exploration.chosenPlan.noReturns') ||
											'Sin datos de rentabilidad'}
								</p>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default ExplorationProjectionV2;
